import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { api } from '../services/api';

const RECENT_KEY = 'finbase.opsRecentOrgs';

const loadRecentOrgs = () => {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item && item.id && item.org_name);
  } catch (err) {
    return [];
  }
};

const saveRecentOrgs = (org) => {
  try {
    const existing = loadRecentOrgs().filter((item) => item.id !== org.id);
    const next = [org, ...existing].slice(0, 3);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch (err) {
    // ignore
  }
};

const OpsOrgSelect = () => {
  const history = useHistory();
  const [recentOrgs, setRecentOrgs] = useState(() => loadRecentOrgs());
  const [directoryOrgs, setDirectoryOrgs] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState('');
  const [directorySearch, setDirectorySearch] = useState('');
  const [directoryType, setDirectoryType] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [paidSubscriptionOnly, setPaidSubscriptionOnly] = useState(false);
  const [downgradeScheduledOnly, setDowngradeScheduledOnly] = useState(false);
  const [cancellationScheduledOnly, setCancellationScheduledOnly] = useState(false);
  const [directorySort, setDirectorySort] = useState('org_name');
  const [directoryOrder, setDirectoryOrder] = useState('asc');
  const [directoryPage, setDirectoryPage] = useState(1);
  const [directoryTotal, setDirectoryTotal] = useState(0);

  const handleSelect = (org) => {
    if (!org?.id) return;
    saveRecentOrgs(org);
    setRecentOrgs(loadRecentOrgs());
    history.push(`/admin/org/${org.id}`);
  };

  useEffect(() => {
    let mounted = true;
    const loadDirectory = async () => {
      setDirectoryLoading(true);
      setDirectoryError('');
      try {
        const response = await api.get('/acct-mgmt/v1/orgs/directory', {
          params: {
            search: directorySearch.trim() || undefined,
            org_type: directoryType || undefined,
            org_status: activeOnly ? 'active' : undefined,
            paid_subscription: paidSubscriptionOnly || undefined,
            scheduled_downgrade: downgradeScheduledOnly || undefined,
            scheduled_cancel: cancellationScheduledOnly || undefined,
            sort: directorySort,
            order: directoryOrder,
            page: directoryPage,
            page_size: 50,
          },
        });
        if (!mounted) return;
        setDirectoryOrgs(response.data?.results || []);
        setDirectoryTotal(response.data?.total || 0);
      } catch (err) {
        if (!mounted) return;
        setDirectoryError(err.response?.data?.error || 'Failed to load organization directory');
      } finally {
        if (mounted) setDirectoryLoading(false);
      }
    };
    loadDirectory();
    return () => {
      mounted = false;
    };
  }, [directorySearch, directoryType, activeOnly, paidSubscriptionOnly, downgradeScheduledOnly, cancellationScheduledOnly, directorySort, directoryOrder, directoryPage]);

  const totalPages = Math.max(Math.ceil(directoryTotal / 50), 1);
  const formatOrgType = (value) => {
    if (value === 'type-org') return 'Organization';
    if (value === 'type-ind') return 'Individual';
    return value || '—';
  };

  const renderStatusBadge = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'closed') {
      return <span className="badge bg-secondary">Closed</span>;
    }
    return <span className="badge bg-success">Active</span>;
  };

  const formatDateOnly = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US');
  };
  const getSubscriptionBadge = (org) => {
    if (org?.subscription_status === 'scheduled_cancel') {
      return <span className="badge bg-danger">Cancellation Scheduled</span>;
    }
    if (org?.subscription_status === 'scheduled_downgrade') {
      return <span className="badge bg-warning text-dark">Downgrade Scheduled</span>;
    }
    return null;
  };

  const renderSubscriptionSummary = (org) => {
    if (!org) return '—';
    if (org.subscription_plan_code === 'trial' || org.subscription_status === 'trialing') {
      return 'Trial';
    }
    if (!org.subscription_plan_name) {
      return 'None';
    }
    const parts = [org.subscription_plan_name];
    if (org.subscription_billing_cadence) {
      parts.push(org.subscription_billing_cadence.charAt(0).toUpperCase() + org.subscription_billing_cadence.slice(1));
    }
    const summary = parts.join(' / ');
    const badge = getSubscriptionBadge(org);
    return (
      <div className="d-flex flex-wrap align-items-center gap-2">
        <span>{summary}</span>
        {badge}
      </div>
    );
  };

  const toggleSort = (sortKey) => {
    if (directorySort === sortKey) {
      setDirectoryOrder(directoryOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setDirectorySort(sortKey);
      setDirectoryOrder('asc');
    }
    setDirectoryPage(1);
  };

  const renderSortableHeader = (label, sortKey) => {
    const isActive = directorySort === sortKey;
    const directionIcon = directoryOrder === 'asc' ? '▲' : '▼';
    return (
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none text-reset fw-semibold"
        onClick={() => toggleSort(sortKey)}
        aria-sort={isActive ? (directoryOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span>{label}</span>
        <span className={`ms-1 ${isActive ? '' : 'text-muted'}`} aria-hidden="true">
          {isActive ? directionIcon : '↕'}
        </span>
      </button>
    );
  };

  return (
    <div>
      <h2 className="h4 fw-bold mb-1">Client Support</h2>
      <p className="text-muted mb-3">Choose an organization to begin support actions.</p>
      {recentOrgs.length > 0 && (
        <div className="card mb-4">
          <div className="card-body">
            <div className="card-title d-flex align-items-center justify-content-between w-100 mb-3">
              <h5 className="mb-0">Recent Organizations</h5>
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => {
                  window.localStorage.removeItem(RECENT_KEY);
                  setRecentOrgs([]);
                }}
              >
                Clear
              </button>
            </div>
            <div className="list-group">
              {recentOrgs.map((org) => (
                <button
                  type="button"
                  key={org.id}
                  className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                  onClick={() => handleSelect(org)}
                >
                  <div>
                    <div className="fw-semibold">{org.org_name}</div>
                    <div className="small text-muted">Org ID: {org.id}</div>
                  </div>
                  <span className="btn btn-sm btn-outline-primary">Select</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Organization Directory</h5>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label">Search</label>
              <input
                className="form-control"
                value={directorySearch}
                onChange={(e) => {
                  setDirectorySearch(e.target.value);
                  setDirectoryPage(1);
                }}
                placeholder="Search by organization name or ID"
              />
              <div className="form-text text-muted">Enter at least 2 characters for targeted search.</div>
            </div>
            <div className="col-md-3">
              <label className="form-label">Organization Type</label>
              <select
                className="form-select"
                value={directoryType}
                onChange={(e) => {
                  setDirectoryType(e.target.value);
                  setDirectoryPage(1);
                }}
              >
                <option value="">All Types</option>
                <option value="type-org">Organization</option>
                <option value="type-ind">Individual</option>
              </select>
            </div>
            <div className="col-md-12">
              <div className="d-flex flex-wrap justify-content-start gap-4 mt-1">
                <div className="form-check form-switch finbase-toggle">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="activeOnly"
                    checked={activeOnly}
                    onChange={(e) => {
                      setActiveOnly(e.target.checked);
                      setDirectoryPage(1);
                    }}
                  />
                  <label className="form-check-label" htmlFor="activeOnly">
                    Active Only
                  </label>
                </div>
                <div className="form-check form-switch finbase-toggle">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="paidSubscriptionOnly"
                    checked={paidSubscriptionOnly}
                    onChange={(e) => {
                      setPaidSubscriptionOnly(e.target.checked);
                      setDirectoryPage(1);
                    }}
                  />
                  <label className="form-check-label" htmlFor="paidSubscriptionOnly">
                    Paid Subscription Only
                  </label>
                </div>
                <div className="form-check form-switch finbase-toggle">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="downgradeScheduledOnly"
                    checked={downgradeScheduledOnly}
                    onChange={(e) => {
                      setDowngradeScheduledOnly(e.target.checked);
                      setDirectoryPage(1);
                    }}
                  />
                  <label className="form-check-label" htmlFor="downgradeScheduledOnly">
                    Downgrade Scheduled
                  </label>
                </div>
                <div className="form-check form-switch finbase-toggle">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="cancellationScheduledOnly"
                    checked={cancellationScheduledOnly}
                    onChange={(e) => {
                      setCancellationScheduledOnly(e.target.checked);
                      setDirectoryPage(1);
                    }}
                  />
                  <label className="form-check-label" htmlFor="cancellationScheduledOnly">
                    Cancellation Scheduled
                  </label>
                </div>
              </div>
            </div>
          </div>

          {directoryError && <div className="alert alert-danger" role="alert">{directoryError}</div>}
          {directoryLoading ? (
            <div className="text-muted">Loading organizations...</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>{renderSortableHeader('Organization Name', 'org_name')}</th>
                    <th>{renderSortableHeader('Organization Type', 'org_type')}</th>
                    <th>{renderSortableHeader('Status', 'org_status')}</th>
                    <th>{renderSortableHeader('Registration Date', 'created_at')}</th>
                    <th>{renderSortableHeader('# Users', 'user_count')}</th>
                    <th>{renderSortableHeader('Subscription', 'subscription')}</th>
                  </tr>
                </thead>
                <tbody>
                  {directoryOrgs.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-muted text-center">No organizations found.</td>
                    </tr>
                  )}
                  {directoryOrgs.map((org) => (
                    <tr
                      key={org.org_id}
                      role="button"
                      tabIndex={0}
                      className="align-middle"
                      onClick={() => handleSelect({ id: org.org_id, org_name: org.org_name })}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleSelect({ id: org.org_id, org_name: org.org_name });
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{org.org_name}</td>
                      <td>{formatOrgType(org.org_type)}</td>
                      <td>{renderStatusBadge(org.org_status)}</td>
                      <td>{formatDateOnly(org.created_at)}</td>
                      <td>{org.user_count}</td>
                      <td>{renderSubscriptionSummary(org)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted small">
              {directoryTotal === 0
                ? 'Showing 0 of 0'
                : `Showing ${(directoryPage - 1) * 50 + 1}–${Math.min(directoryPage * 50, directoryTotal)} of ${directoryTotal}`}
            </div>
            <div className="btn-group">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setDirectoryPage((prev) => Math.max(prev - 1, 1))}
                disabled={directoryPage <= 1}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setDirectoryPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={directoryPage >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpsOrgSelect;
